import { ErrorAlert, Success } from "../components/alerts";
import { SpinButton } from "@/components/form/SpinButton";
import { TestRequest } from "@/api/williams/tests";
import { TestRequestSchema } from "./StandardTestSchema";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { useSubmitRequest } from "./hooks";
import { useWeldingSpecialists } from "../users/hooks";
import { zodResolver } from "@hookform/resolvers/zod";

export function RequestForm() {
  const { state } = useLocation();

  const form = useForm<TestRequest>({
    reValidateMode: "onChange",
    resolver: zodResolver(TestRequestSchema),
    defaultValues: {
      contractor: state?.contractor?.name,
    },
  });

  const errors = form.formState.errors;
  const submit = useSubmitRequest();
  const specialists = useWeldingSpecialists();
  const isDirty = Object.keys(form.formState.dirtyFields).length > 0;

  async function handleSubmitRequest(request: TestRequest) {
    await submit.mutateAsync(request);
    form.reset();
  }

  function handleSelectWeldingSpecialist(id: string) {
    if (specialists.data) {
      const specialist = specialists.data.find((x) => x.id === id);
      form.clearErrors("weldingSpecialist");
      form.setValue("weldingSpecialist", specialist, {
        shouldDirty: true,
      });
    }
  }

  return (
    <div style={{ maxWidth: 850 }}>
      <h3>Request Test</h3>

      <form onSubmit={form.handleSubmit(handleSubmitRequest)} autoComplete="off">
        <div className="row">
          {/* First Column */}
          <div className="col-lg-6 pe-md-3 pe-xl-5">
            {/* Project Number */}
            <div>
              <label htmlFor="projectNumber" className="col-form-label pb-0">
                Project Number
              </label>
              <input type="text" className="form-control" {...form.register("projectNumber")} />
            </div>
            {/* Project Name */}
            <div>
              <label htmlFor="projectName" className="col-form-label pb-0">
                Project Name
              </label>
              <input type="text" className="form-control" {...form.register("projectName")} />
            </div>
            {/* Project Description */}
            <div>
              <label htmlFor="projectDescription" className="col-form-label pb-0">
                Project Description
              </label>
              <input type="text" className="form-control" {...form.register("projectDescription")} />
            </div>
            {/* Contractor */}
            <div>
              <label htmlFor="contractor" className="col-form-label pb-0">
                Contractor
              </label>
              <input type="text" className="form-control" {...form.register("contractor")} />
            </div>
            {/* WPS Name */}
            <div>
              <label htmlFor="wps" className="col-form-label pb-0">
                WPS Name
              </label>
              <input type="text" className="form-control" {...form.register("wps")} />
            </div>
            {/* Test Process(es) */}
            <div>
              <label htmlFor="processes" className="col-form-label pb-0">
                Test Process(es)
              </label>
              <input type="text" className="form-control" {...form.register("processes")} />
            </div>
            {/* Test Materials */}
            <div>
              <label htmlFor="materials" className="col-form-label pb-0">
                Test Materials
              </label>
              <input type="text" className="form-control" {...form.register("materials")} />
            </div>
            {/* Test Thickness */}
            <div>
              <label htmlFor="thickness" className="col-form-label pb-0">
                Test Thickness
              </label>
              <input type="text" className="form-control" {...form.register("thickness")} />
            </div>
            {/* Test Diameter */}
            <div>
              <label htmlFor="diameter" className="col-form-label pb-0">
                Test Diameter
              </label>
              <input type="text" className="form-control" {...form.register("diameter")} />
            </div>
          </div>

          {/* Second Column */}
          <div className="col-lg-6 ps-md-3 ps-xl-5">
            {/* Test Position */}
            <div>
              <label htmlFor="position" className="col-form-label pb-0">
                Test Position
              </label>
              <input type="text" className="form-control" {...form.register("position")} />
            </div>
            {/* Test Progression */}
            <div>
              <label htmlFor="progression" className="col-form-label pb-0">
                Test Progression
              </label>
              <input type="text" className="form-control" {...form.register("progression")} />
            </div>
            {/* Test Weld Type */}
            <div>
              <label htmlFor="weldType" className="col-form-label pb-0">
                Test Weld Type
              </label>
              <input type="text" className="form-control" {...form.register("weldType")} />
            </div>
            {/* Welding Specialist */}
            <div>
              <label htmlFor="weldingSpecialist" className="col-form-label pb-0">
                Welding Specialist <span className="text-danger">*</span>
              </label>
              <select
                className={`form-select ${errors.weldingSpecialist && "border-danger"}`}
                value={form.getValues("weldingSpecialist.id") ?? undefined}
                onChange={(e) => handleSelectWeldingSpecialist(e.target.value)}
              >
                <option />
                {specialists.data &&
                  specialists.data.map((specialist) => (
                    <option key={specialist.id} value={specialist.id}>
                      {specialist.name ?? specialist.userName}
                    </option>
                  ))}
              </select>
              {errors.weldingSpecialist && <span className="text-danger">{errors.weldingSpecialist.message}</span>}
            </div>
            {/* Your Phone # */}
            <div>
              <label htmlFor="phoneNumber" className="col-form-label pb-0">
                Your Phone #
              </label>
              <input type="text" className="form-control" {...form.register("phoneNumber")} />
            </div>
            {/* Other Test Needs */}
            <div>
              <label htmlFor="otherNeeds" className="col-form-label pb-0">
                Other Test Needs
              </label>
              <input type="text" className="form-control" {...form.register("otherNeeds")} />
            </div>
          </div>
        </div>

        <SpinButton
          type="submit"
          className="btn btn-default mt-2"
          loading={submit.isLoading}
          disabled={!isDirty || submit.isLoading}
        >
          Submit
        </SpinButton>
      </form>

      <div className="mt-3 mb-5">
        {submit.isSuccess && (
          <Success>
            The request has been sent. You will receive a copy by email. If you do not receive an email, check your junk
            mail. Call AI Welding SMEs if email isn't received.
          </Success>
        )}
        {submit.isError && (
          <ErrorAlert>An unknown error occurred. Please re-submit the request or call AI Welding SMEs.</ErrorAlert>
        )}
      </div>
    </div>
  );
}
